import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/language";
import { useState, useEffect, useRef } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import backgroundImageJpg from "@/assets/background/background.jpg";
import backgroundImageWebp from "@/assets/background/background.webp";
import backgroundVideoWebm from "@/assets/background/background.webm";
import backgroundVideoMp4 from "@/assets/background/background.mp4";

export function Hero() {
  const { t, dir } = useLanguage();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Use WebP if browser supports it, otherwise fallback to JPG
  const backgroundImage = backgroundImageWebp || backgroundImageJpg;

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let playAttempted = false;

    const ensureVideoAttributes = () => {
      // Force all mobile-required attributes
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
      video.playsInline = true;
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.setAttribute("x5-video-player-type", "h5");
      video.setAttribute("x5-video-player-fullscreen", "false");
    };

    const attemptPlay = async () => {
      if (playAttempted) return;
      playAttempted = true;

      ensureVideoAttributes();

      try {
        video.load(); // Reload with new attributes
        await video.play();
        setVideoLoaded(true);
        console.log("Video playing successfully");
      } catch (error) {
        console.log("Video autoplay failed:", error);

        // Try one more time after a delay
        setTimeout(async () => {
          try {
            ensureVideoAttributes();
            await video.play();
            setVideoLoaded(true);
            console.log("Video playing after retry");
          } catch (e) {
            console.log("Video playback completely failed:", e);
            // Keep showing background image
          }
        }, 1000);
      }
    };

    // Set attributes immediately
    ensureVideoAttributes();

    // Multiple trigger points for play attempt
    video.addEventListener("loadedmetadata", attemptPlay, { once: true });
    video.addEventListener("canplay", attemptPlay, { once: true });

    // Also try on page visibility change (when user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden && video.paused) {
        ensureVideoAttributes();
        video.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Fallback: try after a short delay
    const timer = setTimeout(() => {
      if (video.readyState >= 2 && !playAttempted) {
        attemptPlay();
      }
    }, 500);

    return () => {
      video.removeEventListener("loadedmetadata", attemptPlay);
      video.removeEventListener("canplay", attemptPlay);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(timer);
    };
  }, []);

  return (
    <section
      className="relative h-[60vh] sm:h-[70vh] min-h-[400px] sm:min-h-[500px] flex items-center bg-cover bg-center"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Background Video */}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          videoLoaded ? "opacity-100" : "opacity-0"
        }`}
        poster={backgroundImage}
        preload="metadata"
        autoPlay
        loop
        muted
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden="true"
        style={{ pointerEvents: "none" }}
      >
        <source src={backgroundVideoWebm} type="video/webm" />
        <source src={backgroundVideoMp4} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/40"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block w-full sm:w-auto bg-black/30 backdrop-blur-sm rounded-xl p-4 sm:p-6 md:p-8 shadow-xl ring-1 ring-white/10">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight px-2 drop-shadow-xl">
              {t("heroTitle")}
            </h1>
            <p className="text-base sm:text-xl text-white/90 mb-6 md:mb-8 leading-relaxed max-w-2xl mx-auto px-4 drop-shadow-md">
              {t("heroSubtitle")}
            </p>
            <Button
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground text-sm sm:text-base w-full sm:w-auto"
              onClick={scrollToContact}
            >
              {t("viewProperties")}
              {dir === "rtl" ? (
                <ArrowLeft size={18} className="ml-2 sm:w-5 sm:h-5" />
              ) : (
                <ArrowRight size={18} className="sm:w-5 sm:h-5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
